import { AttachMoney } from '@mui/icons-material';
import { FormControl, FormHelperText, FormLabel, Grid, MenuItem, Select, TextField, Typography } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { DumpLineItemDto, QuoteDto, DumpRateDto, OrderDto, RegionDto } from '../../../../../dtos';
import { useArchiveDumpLineItemDeleteFromDatabaseByIdMutation, useArchiveLineItemByIdMutation, useGetLineItemHasBeenOrderedQuery, useLazyGetDumpRateQuery, usePatchLineItemByIdMutation } from '../../../../../store/generated/generatedApi';
import { SlimFormHeader } from '../../../../Core/SlimFormHeader';
import { IFormProps, LoadingIndicator, FormSection, FormNumberInput } from '../../../../CoreLib/library';
import { useDumpLineItemForm } from './useDumpLineItemForm';
import { dumpTypes, formatCurrency } from '../../../../../util';
import { EquipmentTypeSelect, SiteSelect, UnitOfMeasureSelect, PoAndJobNumberInput, AddressForm } from '../../../../CommonInputs';
import { IEntityAutocomplete } from '../../../../CommonInputs/IEntityAutocomplete';
import { AddressLookup } from '../../../../CommonInputs/AddressLookup';

export interface IDumpLineItemFormProps extends IFormProps<DumpLineItemDto> {
    quote?: QuoteDto;
    order?: OrderDto;
}

export const DumpLineItemForm: FC<IDumpLineItemFormProps> = (props) => {
    const { isLoading, quote, order } = props;
    const {
        isFormDirty,
        handleSave,
        handleCancel,
        fieldErrors,
        handleQuoteChange,
        handleEquipmentTypeChange,
        handleRateChange,
        handleQuantityChange,
        handleDumpTypeChange,
        handleDumpRateChange,
        handleLineItemMemoChange,
        handleDriverMemoChange,
        handleCustomerJobNumberChange,
        handlePoNumberChange,
        handleIsCustomPoNumberChange,
        handleIsCustomerJobNumberCustomChange,
        handleFormAddressChange,
        handleFormRegionChange,
        formQuote,
        formEquipmentType,
        formDumpRate,
        formRate,
        formQuantity,
        formIsActive,
        formDumpType,
        formLineItemMemo,
        formDriverMemo,
        formCustomerJobNumber,
        formPoNumber,
        formIsCustomPoNumber,
        formIsCustomerJobNumberCustom,
        formQuotePoNumber,
        formQuoteCustomerJobNumber,
        formSite,
        formSiteName,
        formUnitOfMeasure,
        formCost,
        formTotal,
        formAddressLine1,
        formAddressLine2,
        formCity,
        formState,
        formZipCode,
        formCountry,
        formDirections,
        formRegion,
        regions,
    } = useDumpLineItemForm(props, quote);

    const [getDumpSites, { data: dumpSites, isLoading: dumpSitesLoading }] = useLazyGetDumpRateQuery();
    const { data: canDeleteData, error: canDeleteError, isLoading: canDeleteLoading } = useGetLineItemHasBeenOrderedQuery({ id: props.initValues?.id ?? '' });

    const [canDelete, setCanDelete] = useState(true);
    useEffect(() => {
        if (canDeleteError) {
            if ((canDeleteError as any).status === 404) {
                setCanDelete(true);
            }
        }
        if (canDeleteData && canDeleteData === true) {
            setCanDelete(false);
        }
    }, [canDeleteData, canDeleteError]);

    useEffect(() => {
        if (quote && !formQuote) {
            handleQuoteChange(quote);
        }
    }, [formQuote, handleQuoteChange, quote]);

    useEffect(() => {
        if (formDumpType) {
            getDumpSites({ type: formDumpType, searchText: '', sorting: ['TYPE+'], page: 0, pageSize: 100000, includeInactive: false });
        }
    }, [formDumpType, getDumpSites]);

    const currentDumpLineItemId = useMemo(() => {
        return props.initValues?.id;
    }, [props.initValues?.id]);

    if (isLoading || canDeleteLoading) {
        return <LoadingIndicator />;
    }

    return (
        <Grid container direction='column'>
            <SlimFormHeader
                objectName='Dump Line Item'
                permissionsTypeName={'lineItem'}
                icon={<AttachMoney />}
                title={formQuote ? `Dump Line Item for Quote #${formQuote.quoteNumber}` : 'New Dump Line Item'}
                titleMaxWidth="60vw"
                breadcrumbs={
                    order
                        ? [
                            { label: 'Home', navLink: '/' },
                            { label: 'Jobs', navLink: '/jobs' },
                            { label: `Job #${quote?.quoteNumber}`, navLink: `/job/${order?.jobId}` },
                            { label: `Order #${order?.orderNumber}`, navLink: `/job/${order?.jobId}/order/${order?.id}` },
                        ]
                        : [
                            { label: 'Home', navLink: '/' },
                            { label: 'Quotes', navLink: '/quotes' },
                            { label: `${quote?.jobName ? `${quote.jobName} - ` : ''}${quote?.customer?.customerNumber ? `${quote.customer.customerNumber}: ` : ''}${quote?.customer?.name ?? ''} (Quote #${quote?.quoteNumber ?? 'Quote'})`, navLink: `/quote/${quote?.id}` },
                            { label: `Line Items`, navLink: `/quote/${quote?.id}/lineItems` },
                        ]
                }
                isActive={formIsActive}
                id={currentDumpLineItemId}
                isFormDirty={isFormDirty}
                handleCancel={handleCancel}
                handleSave={handleSave}
                entityNameSingular={'dump line item'}
                canSave={true}
                canDelete={canDelete}
                deleteMutation={useArchiveDumpLineItemDeleteFromDatabaseByIdMutation}
                archiveRestoreProps={{
                    restoreMutation: usePatchLineItemByIdMutation,
                    archiveMutation: useArchiveLineItemByIdMutation,
                    isActive: formIsActive,
                }}
            />
            <Grid item container direction='row' xs={12} spacing={2} px={2}>
                <Grid item xs={8}>
                    <FormSection>
                        <Grid item>
                            <Typography variant='h2'>General</Typography>
                        </Grid>
                        <Grid item container direction='row' spacing={3}>
                            <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                                <EquipmentTypeSelect
                                    selectedEquipmentTypeId={formEquipmentType?.id ?? ''}
                                    handleSelectedEquipmentTypeChange={handleEquipmentTypeChange}
                                    required
                                    errorMessage={fieldErrors.get('equipmentTypeId')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                                <FormControl error={!!fieldErrors.get('dumpType')} fullWidth required>
                                    <FormLabel>Type</FormLabel>
                                    <Select value={formDumpType ?? ''} onChange={handleDumpTypeChange}>
                                        {dumpTypes.map((type) => {
                                            return (
                                                <MenuItem key={type} value={type}>
                                                    {type}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                    <FormHelperText>{fieldErrors.get('dumpType')}</FormHelperText>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                                <FormControl error={!!fieldErrors.get('dumpRateId')} fullWidth required disabled={!formDumpType}>
                                    <FormLabel>Site + Pricing</FormLabel>
                                    <IEntityAutocomplete
                                        options={dumpSites?.pageResults}
                                        onChange={(e, value) => {
                                            handleDumpRateChange(value ?? null);
                                        }}
                                        value={formDumpRate}
                                        isLoading={dumpSitesLoading}
                                        getOptionLabel={(option: DumpRateDto) =>
                                            `${option.site?.code} - Cost: ${formatCurrency(option.cost)} - ${option.unitOfMeasure}`
                                        }
                                        disabled={!formDumpType}
                                        error={!!fieldErrors.get('dumpRateId')}
                                    />
                                    <FormHelperText>{fieldErrors.get('dumpRateId')}</FormHelperText>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <PoAndJobNumberInput
                                    formQuotePoNumber={formQuotePoNumber}
                                    formIsCustomPoNumber={formIsCustomPoNumber}
                                    handleIsCustomPoNumberChange={handleIsCustomPoNumberChange}
                                    formPoNumber={formPoNumber}
                                    formQuoteCustomerJobNumber={formQuoteCustomerJobNumber}
                                    formIsCustomerJobNumberCustom={formIsCustomerJobNumberCustom}
                                    formCustomerJobNumber={formCustomerJobNumber}
                                    handleIsCustomerJobNumberCustomChange={handleIsCustomerJobNumberCustomChange}
                                    handleCustomerJobNumberChange={handleCustomerJobNumberChange}
                                    handlePoNumberChange={handlePoNumberChange}
                                    fieldErrors={fieldErrors}
                                />
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Typography variant='h2'>Pricing</Typography>
                        </Grid>
                        <Grid item container direction='row' spacing={3}>
                            <Grid item xs={12} sm={6} md={3} lg={2}>
                                <UnitOfMeasureSelect
                                    selectedUnit={formUnitOfMeasure ?? ''}
                                    handleSelectedUnitChange={() => { }}
                                    errorMessage={fieldErrors.get('unitOfMeasure')}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3} lg={2} xl={2}>
                                <FormNumberInput
                                    value={formCost}
                                    onChange={() => { }}
                                    label='Cost'
                                    name='cost'
                                    fullWidth
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3} lg={2.5} xl={2}>
                                <FormNumberInput
                                    value={formQuantity ?? ''}
                                    onChange={handleQuantityChange}
                                    label='Quantity'
                                    name='quantity'
                                    errorText={fieldErrors.get('quantity')}
                                    error={!!fieldErrors.get('quantity')}
                                    fullWidth
                                    disabled={isLoading}
                                    inputProps={{ min: 0 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3} lg={2.5} xl={2}>
                                <FormNumberInput
                                    value={formRate}
                                    onChange={handleRateChange}
                                    label='Rate'
                                    name='rate'
                                    errorText={fieldErrors.get('rate')}
                                    error={!!fieldErrors.get('rate')}
                                    fullWidth
                                    required
                                    disabled={isLoading}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6} lg={3} xl={2}>
                                <FormNumberInput value={formTotal ?? ''} onChange={() => { }} label='Total' name='total' fullWidth disabled isCurrency />
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Typography variant='h2'>Haul To</Typography>
                        </Grid>
                        <Grid item container direction='row' spacing={3}>
                            <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                                <SiteSelect
                                    selectedSiteId={formSite?.id ?? ''}
                                    handleSelectedSiteChange={() => { }}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <AddressLookup
                                    handleAddressChange={handleFormAddressChange}
                                    key={0}
                                    disabled={isLoading}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <FormControl fullWidth disabled={isLoading}>
                                    <FormLabel>Site Name</FormLabel>
                                    <TextField
                                        value={formSiteName}
                                        name='siteName'
                                        fullWidth
                                        variant='outlined'
                                        disabled
                                        InputProps={{ readOnly: true }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={3}>
                                <FormControl fullWidth>
                                    <FormLabel>Region</FormLabel>
                                    <IEntityAutocomplete
                                        options={regions?.pageResults ?? []}
                                        onChange={(event, value) => handleFormRegionChange(value)}
                                        value={formRegion}
                                        getOptionLabel={(option: RegionDto) => option.name}
                                        disabled={isLoading}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item container direction={'row'} spacing={2} wrap='nowrap'>
                                <AddressForm
                                    handleAddressChange={handleFormAddressChange}
                                    addressLine1={formAddressLine1}
                                    addressLine2={formAddressLine2}
                                    city={formCity}
                                    state={formState}
                                    zipCode={formZipCode}
                                    country={formCountry}
                                    directions={formDirections}
                                    enableLookup={false}
                                    disabled={isLoading}
                                    allOptional
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    value={formLineItemMemo ?? ''}
                                    onChange={handleLineItemMemoChange}
                                    label='Line Item Memo'
                                    name='lineItemMemo'
                                    fullWidth
                                    multiline
                                    minRows={3}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    value={formDriverMemo}
                                    onChange={handleDriverMemoChange}
                                    label='Order Memo'
                                    fullWidth
                                    multiline
                                    minRows={3}
                                />
                            </Grid>
                        </Grid>
                    </FormSection>
                </Grid>
                <Grid item xs={4}>
                    <Grid item container direction='column' spacing={3}>
                        <Grid item>
                            <FormSection>
                                <Grid item>
                                    <Typography variant='h2'>
                                        {!formDumpRate ? 'No Site Selected' : `${formDumpRate?.site?.code} - ${formDumpRate?.site?.name}`}
                                    </Typography>
                                </Grid>
                                {formDumpRate && (
                                    <Grid item container direction='column'>
                                        <Grid item>{formDumpRate.site?.addressLine1}</Grid>
                                        <Grid item>{formDumpRate.site?.addressLine2}</Grid>
                                        <Grid item>
                                            {formDumpRate.site?.city ?? '(City)'}, {formDumpRate.site?.state ?? '(State)'},{' '}
                                            {formDumpRate.site?.zipCode ?? '(Zip Code)'}
                                        </Grid>
                                        <Grid item>{formDumpRate.site?.country ?? '(Country)'}</Grid>
                                    </Grid>
                                )}
                            </FormSection>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};
